import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Chip,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyFmt = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export default function InventoryAging() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    if (!asOf) { setError("Pick a date"); return; }
    setLoading(true); setError(null);
    api.get("/accounting/inventory-aging", { params: { asOf } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build Inventory Aging"))
      .finally(() => setLoading(false));
  }, [asOf]);

  const cell = { py: 0.4, fontVariantNumeric: "tabular-nums" };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Inventory Aging (Dead Stock)</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        How long stock on hand has been sitting, per material, in age buckets. Material aged
        <strong> 90+ days</strong> is capital tied up in a pile that isn't moving. This is an estimate using a
        <strong> synthetic FIFO</strong> (oldest material assumed sold first) — there's no lot tracking, so exact
        ages aren't possible, but the totals reconcile with your Inventory Valuation. Read-only.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="As of" InputLabelProps={{ shrink: true }}
            value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Inventory Aging</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>As of {data.asOf}</Typography>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }} flexWrap="wrap">
            <Chip label={`${data.itemCount} materials on hand`} />
            <Chip label={`Total: ${qtyFmt(data.totals.total)} lb`} />
            <Chip color={data.totals.b90 > 0 ? "error" : "success"}
              label={`90+ days: ${qtyFmt(data.totals.b90)} lb${data.totals.value90 ? ` ($${money(data.totals.value90)})` : ""}`} />
          </Stack>
          <Divider sx={{ mb: 1 }} />

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>On hand (lb)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>0&ndash;30</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>31&ndash;60</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>61&ndash;90</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "error.main" }}>90+ (stale)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "error.main" }}>$ stuck in 90+</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ color: "text.secondary" }}>No stock on hand.</TableCell></TableRow>
                )}
                {data.rows.map((r, i) => (
                  <TableRow key={`${r.item}-${i}`} sx={r.b90 > 0 ? { bgcolor: "rgba(198,40,40,0.06)" } : undefined}>
                    <TableCell sx={{ py: 0.4 }}>{r.item}</TableCell>
                    <TableCell align="right" sx={cell}>{qtyFmt(r.total)}</TableCell>
                    <TableCell align="right" sx={cell}>{r.b0 ? qtyFmt(r.b0) : "—"}</TableCell>
                    <TableCell align="right" sx={cell}>{r.b31 ? qtyFmt(r.b31) : "—"}</TableCell>
                    <TableCell align="right" sx={cell}>{r.b61 ? qtyFmt(r.b61) : "—"}</TableCell>
                    <TableCell align="right" sx={{ ...cell, fontWeight: r.b90 > 0 ? 700 : 400, color: r.b90 > 0 ? "error.main" : "text.disabled" }}>
                      {r.b90 ? qtyFmt(r.b90) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cell, color: r.b90 > 0 ? "error.main" : "text.disabled" }}>
                      {r.value90 == null ? "n/a" : r.b90 > 0 ? money(r.value90) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800 }}>Totals</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, ...cell }}>{qtyFmt(data.totals.total)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, ...cell }}>{qtyFmt(data.totals.b0)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, ...cell }}>{qtyFmt(data.totals.b31)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, ...cell }}>{qtyFmt(data.totals.b61)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, color: "error.main", ...cell }}>{qtyFmt(data.totals.b90)}</TableCell>
                  <TableCell align="right" sx={{ borderTop: "2px solid", borderColor: "text.primary", py: 1, fontWeight: 800, color: "error.main", ...cell }}>{money(data.totals.value90)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
            Age is measured from the date material entered stock (purchase, regrade-in, or wire-chop output).
            Opening balances have no date and are treated as oldest (90+). "$ stuck in 90+" values the stale
            weight at weighted-average purchase cost; materials never purchased (processing outputs) show "n/a".
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
